import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CommonService } from '../../services/common/common.service';
import { ApiUrlHelper } from '../../common/api-url-helper';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { SharedService } from '../shared-service/shared.service';
import { StorageService } from '../storage/storage.service';

interface MenuItem {
  id: number;
  name: string;
  icon: string;
  url: string;
  isParent: boolean;
  parentId: number;
  isForm: boolean;
  formId: number;
  subMenus?: MenuItem[];
  subItems?: MenuItem[];
  isOpen?: boolean;
  isNotePending:boolean;
}

@Injectable({
  providedIn: 'root',
})
export class RoleRightsService  {
  private roles: any[] = []; // List of roles with permissions
  private menuItems: MenuItem[] = []; // Cached menu items
  dataFetched: boolean = false; // Flag to check if data has been fetched
  private fetchInProgress: Promise<void> | null = null; // To track ongoing fetch

  constructor(
    private readonly commonService: CommonService,
    private readonly apiUrl: ApiUrlHelper,
    private readonly spinner: NgxSpinnerService,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly shareService: SharedService,
    private readonly storageService: StorageService
  ) {}
  

  private async fetchData(): Promise<void> {
    if (this.fetchInProgress) {
      // If a fetch is already in progress, wait for it to complete
      return this.fetchInProgress;
    }
    if (this.dataFetched) {
      // Data is already fetched
      return;
    }
    this.spinner.show();
    this.fetchInProgress = (async () => {
      try {
        const [roleResponse, menuResponse] = await Promise.all([
          this.commonService
            .doGet(this.apiUrl.apiUrl.Role.GetRoleRightList)
            .toPromise(),
          this.commonService
            .doGet(this.apiUrl.apiUrl.MenuList.GetMenuList)
            .toPromise(),
        ]);
        if (roleResponse?.success) {
          this.roles = this.parseRoles(roleResponse.data);
        } else {
          this.toastr.error('Failed to fetch role rights');
        }
        if (menuResponse?.success) {
          this.menuItems = this.parseMenuItems(menuResponse.data);
          this.shareService.setIsNotePending(this.menuItems[0].isNotePending);
        } else {
          this.toastr.error('Failed to fetch menu list');
        }
        this.dataFetched = true; // Set flag to true after data fetch
      } catch (err) {
        this.toastr.error('Error fetching data');
      } finally {
        this.spinner.hide();
        this.fetchInProgress = null; // Reset fetchInProgress after completion
      }
    })();
    return this.fetchInProgress;
  }

  private parseRoles(data: any[]): any[] {
    return data.reduce((roles: any[], item: any) => {
      const existingRole = roles.find((role) => role.roleId === item.roleId);
      const menuDetails = JSON.parse(item.menuDetails);
      const modules = menuDetails.map((menu: any) => ({
        id: menu.Id,
        menuId: menu.MenuId,
        moduleName: menu.MenuName,
        isView: menu.IsView,
        isAdd: menu.IsAdd,
        isEdit: menu.IsEdit,
        isDelete: menu.IsDelete,
        selectedAll: menu.SelectedAll === 1,
      }));

      if (existingRole) {
        existingRole.modules.push(...modules);
      } else {
        roles.push({
          roleId: item.roleId,
          name: item.roleName,
          modules: modules,
        });
      }
      return roles;
    }, []);
  }

  private parseMenuItems(response: any[]): MenuItem[] {
    return response.map((item) => ({
      id: item.parentMenuId,
      name: item.parentMenuName,
      icon: item.icon,
      url: item.url,
      isParent: item.items !== null,
      parentId: 0,
      isForm: false,
      formId: 0,
      subMenus: item.items ? JSON.parse(item.items) : [],
      isOpen: false,
      isNotePending:item.isNotePending
    }));
  }

  async hasAccess(
    permission: 'isView' | 'isAdd' | 'isEdit' | 'isDelete'
  ): Promise<boolean> {
    if (!this.storageService.getValue('authToken')) {
      return false;
    }
    await this.fetchData(); // Ensure data is fetched
    const url = this.router.url;
    const menu = this.findMenuByUrl(url);
    if (menu) {
      const role = this.roles.find((r) =>
        r.modules.some((m: any) => m.menuId === menu.id)
      );
      if (role) {
        const module = role.modules.find((m: any) => m.menuId === menu.id);
        this.shareService.setPermission(module.isAdd);
        this.shareService.sethavePermission(
          (module.isAdd ?? 0) || (module.isEdit ?? 0)
        );
        this.shareService.setdeletePermission(module.isDelete);
        this.shareService.sethaveEditPermission(module.isEdit);
        this.shareService.sethaveAddPermission(module.isAdd);
        this.shareService.sethaveOnlyViewPermission(
          !module.isAdd && !module.isEdit && !module.isDelete && module.isView
        );
        return module ? module[permission] : false;
      }
    }
    return false;
  }

  private findMenuByUrl(url: string): MenuItem | undefined {
    const segments = url.split('/').filter((segment) => segment);

    let currentMenus = this.menuItems;
    for (let i = 0; i < segments.length; i++) {
      const segment = '/' + segments[i];
      const menu = currentMenus.find((m) => m.url === segment);
      if (!menu) {
        return undefined;
      }
      if (i === segments.length - 1) {
        return menu;
      }
      currentMenus = menu.subMenus || menu?.subItems || [];
    }
    return undefined;
  }
}
